<template>
  <loader v-bind="{ loading }">
    <div class="mb-5">
      <div class="columns">
        <column class="is-3">
          <h3 class="is-size-4">Personal Information</h3>
          <p class="has-text-grey">Security settings and personal information.</p>
        </column>
        <div class="column">
          <div class="box">
            <columns>
              <column>
                <columns>
                  <column>
                    <text-input 
                      required 
                      :value="user.name"
                      @input="updateName"
                      :error="$root.errorBag.name">
                      Name
                    </text-input>
                  </column>
                  <column>
                    <text-input 
                      :value="user.mobile"
                      @input="updateMobile"
                      :error="$root.errorBag.mobile">
                      Mobile
                    </text-input>
                  </column>
                </columns>
                <columns>
                  <column>
                    <action-button :working="savingProfile" @click="updateProfile">Save</action-button>
                  </column>
                </columns>
              </column>
              <column class="is-narrow">
                <div class="is-flex justify-end align-center">
                  <div class="is-flex flex-column align-center justify-center">
                    <div @click="selectPicture">
                      <avatar colorize class="cursor-pointer" :size="80" :name="user.name" :path="user.profile_picture_url"> </avatar>
                    </div>
                    <action-button class="mt is-small is-rounded" left-icon="pencil-alt" @click="selectPicture">
                      Change Picture
                    </action-button>
                    <small>Optimal size: 400px by 400px</small>
                  </div>
                </div>
              </column>
            </columns>

            <columns>
              <column>
                <p class="block">Security</p>
                <action-button v-if="!user.two_factor_is_setup" @click="setupTwoFactorAuth">Enable Two Factor Authentication</action-button>
                <action-button :working="disabling" v-else @click="disableTwoFactorAuth">Disable Two Factor Authentication</action-button>
              </column>
            </columns>
          </div>
        </div>
      </div>

      <div class="columns">
        <column class="is-3">
          <h3 class="is-size-4">Account Information</h3>
          <p class="has-text-grey">Manage your invoice and business information.</p>
        </column>
        <div class="column">
          <div class="box">
            <columns>
              <column>
                <text-input 
                  :error="$root.errorBag.company_name"
                  :value="account.company_name"
                  @input="updateCompanyName">Company Name</text-input>
              </column>
              <column>
                <text-input
                  :error="$root.errorBag.registration_number"
                  :value="account.registration_number"
                  @input="updateRegNumber">Company Reg Number</text-input>
              </column>
              <column>
                <text-input 
                  :error="$root.errorBag.gst_number"
                  :value="account.gst_number"
                  @input="updateGstNumber"
                  hint="Only for NZ based customers">GST Number</text-input>
              </column>
            </columns>

            <columns>
              <column>
                <text-input
                  :error="$root.errorBag['address.street']"
                  :value="account.address.street"
                  @input="updateStreet">Street</text-input>
              </column>
              <column>
                <text-input
                  :error="$root.errorBag['address.city']"
                  :value="account.address.city"
                  @input="updateCity">
                  City
                </text-input>
              </column>
            </columns>
            <columns>
              <column>
                <text-input
                  :error="$root.errorBag['address.province']"
                  :value="account.address.province"
                  @input="updateProvince">
                  State/Province
                </text-input>
              </column>
              <column>
                <select-input
                  :items="countries"
                  label-key="name"
                  value-key="id"
                  :error="$root.errorBag['address.country_id']"
                  :value="account.address.country_id"
                  @input="updateCountry">
                  Country
                </select-input>
              </column>
              <column>
                <text-input
                  :error="$root.errorBag['address.post_code']"
                  :value="account.address.post_code"
                  @input="updatePostCode">
                  Post Code
                </text-input>
              </column>
            </columns>

            <columns>
              <column>
                <text-input 
                  :error="$root.errorBag.po_number"
                  :value="account.po_number"
                  @input="updatePoNumber"
                  description="Should your company require purchase order numbers for invoices">
                  Purchase Order
                </text-input>
              </column>
              <column>
                <text-input 
                  :error="$root.errorBag.billing_email"
                  :value="account.billing_email"
                  @input="updateBillingEmail"
                  description="Invoices will be rerouted to this email address">
                  Billing Email
                </text-input>
              </column>
            </columns>

            <columns>
              <column>
                <action-button 
                  :working="savingAccount" 
                  @click="saveAccount">
                  Save
                </action-button>
              </column>
            </columns>
          </div>
        </div>
      </div>

      <columns>
        <column class="is-3">
          <h3 class="is-size-4">Billing</h3>
          <p class="has-text-grey">Card and subscription management.</p>
        </column>
        <column>
          <div class="box">
            <columns>
              <column>
                <router-link v-if="!account.subscription.id" :to="{
                  name: 'plans'
                }">Select A Plan</router-link>
                <subscription-quick-view v-else />
              </column>
              <column>
                <div class="has-text-right" v-if="card && card.id">
                  <span>{{ card.brand }} {{ `**** **** **** ${card.last_four}`}}</span>
                </div>
              </column>
            </columns>

            <h4 class="title is-5">Invoices</h4>

            <table class="table is-striped is-fullwidth">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Date</th>
                  <th class="is-narrow">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="invoice in invoices" :key="invoice.id">
                  <td>{{ invoice.id }}</td>
                  <td>{{ invoice | date }}</td>
                  <td>
                    <action-button :disabled="downloadingInvoices" @click="downloadInvoice(invoice.id)" class="is-small is-rounded">Download</action-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </column>
      </columns>

      <div class="push-down"></div>
    </div>
  </loader>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex'
import filePicker from 'file-dialog'
import { cropImage, twoFactorSetup } from '@/modals'
import { get } from '@/api/request'
import { downloadFile } from '@/api/common'
import SubscriptionQuickView from './partials/SubscriptionQuickView'
import download from 'downloadjs'

export default {

  components: {
    SubscriptionQuickView
  },

  data: () => ({
    loading: true,
    savingAccount: false,
    savingProfile: false,
    countries: [],
    disabling: false,
    downloadingInvoices: false,
  }),

  beforeCreate() {
    Promise.all([
      get('api/countries', ({data}) => this.countries = data),
      this.$store.dispatch('auth/loadCard'),
      this.$store.dispatch('auth/loadAccount'),
      this.$store.dispatch('auth/loadLatestInvoices'),
    ]).then(() => {
      this.loading = false
    })
  },

  mounted() {
    this.$bus.$emit('set-title', 'Account')
  },

  methods: {
    ...mapMutations('auth', [
      'updateName',
      'updateMobile',
      'updateCompanyName',
      'updateRegNumber',
      'updateGstNumber',
      'updatePoNumber',
      'updateBillingEmail',
      'updateStreet',
      'updateSuburb',
      'updateCity',
      'updateProvince',
      'updatePostCode',
      'updateCountry',
    ]),
    async selectPicture() {
      let files = await filePicker({
        accept: 'image/*',
        multiple: false,
      })
      if (files && files.length) {
        let image = new Image()
        image.onload = () => {
          if (image.width > 250 || image.height > 250) {
            this.cropAndUpload(files[0])
          } else {
            this.$alert('Picture too small', 'Please select a picture that is at least 250px × 250px.')
          }
        }
        image.onerror = () => {
          this.$toast.error('That file could not be selected.')
        }
        image.src = URL.createObjectURL(files[0])
      }
    },

    async cropAndUpload(image) {
      if (await cropImage('Crop your Profile Picture', image, this.uploadImage)) {
        this.$toast.success('Profile picture updated successfully.')
      }
    },

    async uploadImage(file) {
      this.$store.dispatch('auth/uploadProfilePicture', {file})
    },

    saveAccount() {
      this.savingAccount = true
      this.$store.dispatch('auth/updateAccount').then(() => {
        this.savingAccount = false
        this.$toast.success('Saved')
      }).catch(error => {
        this.savingAccount = false
      })
    },

    updateProfile() {
      this.savingProfile = true
      this.$store.dispatch('auth/updateProfile').then(() => {
        this.savingProfile = false
        this.$toast.success('Saved')
      }).catch(error => {
        this.savingProfile = false
      })
    },

    setupTwoFactorAuth() {
      this.enabling = true
      this.$store.dispatch('auth/enable2Fa').then(() => {
        twoFactorSetup().then((result) => {
          if(!result) {
            this.$store.dispatch('auth/disable2Fa')
          }
        })
      }).catch(error => {
        console.log(error)
        this.enabling = false
      })
    },

    async disableTwoFactorAuth() {
      if(await this.$confirm({
        title: 'Disable Two Factor Authentication',
        message: 'Are you sure you want to disable Two Factor Authentication? This is not recommended.'
      })) {
        this.disabling = true
        this.$store.dispatch('auth/disable2Fa').then(() => {
          this.disabling = false
        }).catch(error => {
          this.disabling = false
        })
      }
    },

    downloadInvoice(invoiceId) {
      this.downloadingInvoices = true
      downloadFile(`/api/invoice/${invoiceId}/download`, ({data}) => {
        this.downloadingInvoices = false
        download(data, 'snitch-invoice-' + invoiceId, 'application/pdf')
      }, error => {
        this.downloadingInvoices = false
      })
    }
  },

  computed: {
    ...mapGetters('auth', [
      'user', 
      'account', 
      'plans',
      'card',
      'invoices'
    ]),
  },
}
</script>
